import React, { useState, useEffect, createContext, useContext } from "react";

// 支持的 AI 厂商
export const AI_PROVIDERS = [
  {
    id: "openai",
    name: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    apiKeyPlaceholder: "sk-...",
  },
  {
    id: "anthropic",
    name: "Anthropic",
    baseUrl: "https://api.anthropic.com",
    apiKeyPlaceholder: "sk-ant-...",
  },
  {
    id: "deepseek",
    name: "DeepSeek",
    baseUrl: "https://api.deepseek.com/v1",
    apiKeyPlaceholder: "sk-...",
  },
  {
    id: "doubao",
    name: "豆包 (Doubao)",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3",
    apiKeyPlaceholder: "ak-...",
  },
  {
    id: "qwen",
    name: "通义千问 (Qwen)",
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    apiKeyPlaceholder: "sk-...",
  },
  {
    id: "custom",
    name: "自定义 (Custom)",
    baseUrl: "",
    apiKeyPlaceholder: "输入 API Key",
  },
];

export type AIProvider = typeof AI_PROVIDERS[number];

export interface ApiConfig {
  providerId: string;
  apiKey: string;
  baseUrl: string;
  model: string;
}

const STORAGE_KEY = "excalidraw_ai_api_config";

export const loadApiConfig = (): ApiConfig | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error("Failed to load API config:", error);
  }
  return null;
};

export const saveApiConfig = (config: ApiConfig) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error("Failed to save API config:", error);
  }
};

// 上下文用于全局管理对话框状态
interface ApiSettingsContextType {
  isOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;
}

const ApiSettingsContext = createContext<ApiSettingsContextType | undefined>(
  undefined,
);

// Provider 组件
export const ApiSettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openSettings = () => setIsOpen(true);
  const closeSettings = () => setIsOpen(false);

  return (
    <ApiSettingsContext.Provider
      value={{ isOpen, openSettings, closeSettings }}
    >
      {children}
      {isOpen && <ApiKeySettingsDialog onClose={closeSettings} />}
    </ApiSettingsContext.Provider>
  );
};

// Hook 来使用上下文
export const useApiSettings = () => {
  const context = useContext(ApiSettingsContext);
  if (!context) {
    throw new Error("useApiSettings must be used within ApiSettingsProvider");
  }
  return context;
};

// API 设置对话框组件
const ApiKeySettingsDialog = ({ onClose }: { onClose: () => void }) => {
  const [config, setConfig] = useState<ApiConfig>(() => {
    const saved = loadApiConfig();
    return (
      saved || {
        providerId: "openai",
        apiKey: "",
        baseUrl: AI_PROVIDERS[0].baseUrl,
        model: "gpt-4",
      }
    );
  });

  const [showCustomUrl, setShowCustomUrl] = useState(
    config.providerId === "custom",
  );

  const currentProvider = AI_PROVIDERS.find((p) => p.id === config.providerId);

  useEffect(() => {
    setShowCustomUrl(config.providerId === "custom");
    if (config.providerId !== "custom") {
      const provider = AI_PROVIDERS.find((p) => p.id === config.providerId);
      if (provider) {
        setConfig((prev) => ({ ...prev, baseUrl: provider.baseUrl }));
      }
    }
  }, [config.providerId]);

  const handleSave = () => {
    saveApiConfig(config);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "24px",
          minWidth: "400px",
          maxWidth: "500px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        <h2
          style={{
            margin: "0 0 20px 0",
            fontSize: "20px",
            fontWeight: "bold",
            color: "#1a1a1a",
          }}
        >
          ⚙️ AI API 设置
        </h2>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            AI 厂商
          </label>
          <select
            value={config.providerId}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, providerId: e.target.value }))
            }
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box" as const,
            }}
          >
            {AI_PROVIDERS.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            API Key
          </label>
          <input
            type="password"
            value={config.apiKey}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, apiKey: e.target.value }))
            }
            placeholder={currentProvider?.apiKeyPlaceholder}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box" as const,
            }}
          />
        </div>

        {showCustomUrl && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#333",
              }}
            >
              API 地址 (Base URL)
            </label>
            <input
              type="text"
              value={config.baseUrl}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, baseUrl: e.target.value }))
              }
              placeholder="https://api.example.com/v1"
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box" as const,
              }}
            />
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#333",
            }}
          >
            模型名称
          </label>
          <input
            type="text"
            value={config.model}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, model: e.target.value }))
            }
            placeholder="gpt-4, claude-3-opus, deepseek-chat 等"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box" as const,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              border: "1px solid #d9d9d9",
              backgroundColor: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              color: "#333",
            }}
          >
            取消
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              border: "none",
              backgroundColor: "#1890ff",
              color: "white",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};
